import { worldScaleUVs } from './worldUv';
/**
 * The bridge world: recycled segments + water plane. sim z maps directly to world Z;
 * sim x ∈ [-1, 1] maps to world X = x * BRIDGE_HALF_WIDTH.
 */
import { Color3, Mesh, MeshBuilder, PBRMaterial, StandardMaterial, TransformNode, VertexBuffer, VertexData } from '@babylonjs/core/pure';
import type { Scene, Texture } from '@babylonjs/core/pure';
import { buildDressing, tint } from './dressing';
import { buildStructures } from './structures';
import { hashValue } from './scene';
import type { EnvTheme, RenderMaterials } from './scene';
import type { QualityTier } from '../gfx/quality';

export const BRIDGE_HALF_WIDTH = 4.5;
export const SEGMENT_LENGTH = 40;
const WATER_Y = -42;
const WATER_SIZE = 2400;
const WATER_TILE = 14; // metres per normal-map repeat
const PROP_DISTANCE = 70; // segment start distance; even the nearest hidden prop is well ahead
const WATER_FLOW = 0.35; // m/s the detail normals drift (a function of sim.t)

export class Bridge {
  private readonly segmentRoots: TransformNode[] = [];
  private readonly propBatches: Array<Mesh | undefined> = [];
  private readonly concreteBatches: Mesh[] = [];
  private readonly steelBatches: Array<Mesh | undefined> = [];
  private nextIndex = 0;
  private nextStartZ = 0;
  private water!: Mesh;
  private horizon!: Mesh;
  private readonly propMaterial: PBRMaterial;

  constructor(
    private readonly scene: Scene,
    private readonly materials: RenderMaterials,
    segmentCount: number,
    private readonly theme: EnvTheme = 'overcast',
    private readonly tier?: QualityTier,
  ) {
    this.propMaterial = new PBRMaterial('bridge-prop', scene);
    this.propMaterial.metallic = 0;
    this.propMaterial.roughness = 0.9;
    for (let i = 0; i < segmentCount; i++) this.segmentRoots.push(this.buildSegment(i));
    this.reset();
    this.buildWater();
    this.buildHorizon();
  }

  /** Back to the boot layout from z = 0. Restart needs it: `update` only ever recycles forward. */
  reset(): void {
    this.segmentRoots.forEach((root, i) => this.place(root, i * SEGMENT_LENGTH));
    this.nextStartZ = this.segmentRoots.length * SEGMENT_LENGTH;
    this.nextIndex = 0;
    this.updateSceneryVisibility(0);
  }

  /**
   * Keep the segment ring covering [behindZ, ahead of squadZ]; water and horizon ride with the squad.
   * `t` is `sim.t` (never a render-side accumulator), so the water drift is a pure function of the
   * snapshot and freezes with a pause.
   */
  update(squadZ: number, t: number): void {
    this.water.position.z = squadZ;
    this.horizon.position.z = squadZ;
    const bump = this.materials.water.bumpTexture as Texture | null;
    if (bump) {
      // Keep the pattern fixed in world space as the plane slides (+v is +z on a ground), then drift it.
      const frac = (x: number): number => x - Math.floor(x);
      bump.uOffset = frac((t * WATER_FLOW) / WATER_TILE);
      bump.vOffset = frac((squadZ + t * WATER_FLOW * 0.6) / WATER_TILE);
    }
    // Recycle any segment fully behind the squad.
    for (let i = 0; i < this.segmentRoots.length; i++) {
      const root = this.segmentRoots[i];
      if (root.position.z + SEGMENT_LENGTH < squadZ - 30) {
        this.place(root, this.nextStartZ + this.nextIndex * SEGMENT_LENGTH);
        this.nextIndex++;
      }
    }
    this.updateSceneryVisibility(squadZ);
  }

  private updateSceneryVisibility(squadZ: number): void {
    if (this.tier !== 'low' && this.tier !== 'medium') return;
    for (let i = 0; i < this.segmentRoots.length; i++) {
      const distance = this.segmentRoots[i]!.position.z - squadZ;
      this.propBatches[i]?.setEnabled(distance <= (this.tier === 'medium' ? 40 : PROP_DISTANCE));
      if (this.tier === 'medium') {
        this.concreteBatches[i]!.setEnabled(distance <= 70);
        this.steelBatches[i]?.setEnabled(distance <= 40);
      }
    }
  }

  /** Probe/test accessor: the segment roots. */
  debugRoots(): TransformNode[] {
    return this.segmentRoots;
  }

  /** Probe: max world-center z of segment CHILD meshes (frozen children cap this on main —
   * plan 03: roots move even when children don't, so roots cannot detect the freeze). */
  debugChildZMax(): number {
    let zMax = -Infinity;
    for (const root of this.segmentRoots) {
      for (const child of root.getChildMeshes()) {
        child.computeWorldMatrix(true);
        const z = child.getBoundingInfo().boundingBox.centerWorld.z;
        zMax = Math.max(zMax, z);
      }
    }
    return zMax;
  }

  private place(root: TransformNode, z: number): void {
    root.position.z = z;
    // Only prop batches mirror; fixed brazier positions must match the ambient effects.
    const mirror = Math.floor(z / (this.segmentRoots.length * SEGMENT_LENGTH)) % 2 === 0 ? 1 : -1;
    for (const child of root.getChildMeshes()) {
      if (child.name.startsWith('bridge-props-') || child.name.startsWith('bridge-steel-')) child.scaling.x = mirror;
    }
  }

  /**
   * One 40 m segment: asphalt deck, concrete skirt + rails + truss posts, pillars every other
   * segment. The legacy path uses two batches; tiered segments use asphalt, concrete, steel and
   * optionally nonmetal props, for at most four draws per segment.
   */
  private buildSegment(index: number): TransformNode {
    const { scene, materials } = this;
    const root = new TransformNode(`seg-root-${index}`, scene);

    const asphaltParts: Mesh[] = [];
    const concreteParts: Mesh[] = [];

    const deck = MeshBuilder.CreateGround(`deck-${index}`, { width: BRIDGE_HALF_WIDTH * 2 + 0.8, height: SEGMENT_LENGTH }, scene);
    deck.position.set(0, 0, SEGMENT_LENGTH / 2);
    asphaltParts.push(deck);

    const skirt = MeshBuilder.CreateBox(`skirt-${index}`, { width: BRIDGE_HALF_WIDTH * 2 + 1.2, height: 1.1, depth: SEGMENT_LENGTH }, scene);
    skirt.position.set(0, -0.62, SEGMENT_LENGTH / 2);
    concreteParts.push(skirt);

    for (const side of [-1, 1]) {
      const rail = MeshBuilder.CreateBox(`rail-${index}-${side}`, { width: 0.22, height: 1.0, depth: SEGMENT_LENGTH }, scene);
      rail.position.set(side * (BRIDGE_HALF_WIDTH + 0.35), 0.5, SEGMENT_LENGTH / 2);
      concreteParts.push(rail);
      // Fence posts (visual rhythm).
      for (let p = 0; p < 4; p++) {
        const post = MeshBuilder.CreateBox(`post-${index}-${side}-${p}`, { width: 0.3, height: 1.35, depth: 0.3 }, scene);
        post.position.set(side * (BRIDGE_HALF_WIDTH + 0.35), 0.67, 5 + p * 10);
        concreteParts.push(post);
      }
    }

    // Full-height clearance keeps portrait encounter framing below the overhead beams.
    const trussHeight = this.tier === undefined ? 5.2 : 7.8;
    for (const side of [-1, 1]) {
      const post = MeshBuilder.CreateBox(`truss-${index}-${side}`, { width: 0.45, height: trussHeight, depth: 0.45 }, scene);
      post.position.set(side * (BRIDGE_HALF_WIDTH + 0.5), trussHeight / 2, SEGMENT_LENGTH);
      post.metadata = { surface: 'metal' };
      concreteParts.push(post);
    }
    const beam = MeshBuilder.CreateBox(`beam-${index}`, { width: BRIDGE_HALF_WIDTH * 2 + 1.5, height: 0.5, depth: 0.5 }, scene);
    beam.position.set(0, trussHeight, SEGMENT_LENGTH);
    beam.metadata = { surface: 'metal' };
    concreteParts.push(beam);

    // Support pillar every other segment.
    if (index % 2 === 0) {
      const pillar = MeshBuilder.CreateCylinder(`pillar-${index}`, { diameter: 2.4, height: 43, tessellation: 10 }, scene); // top at y=-1: a y=0 top is coplanar with the deck and z-fights through it (decagon patch under the squad at z=100)
      pillar.position.set(0, -22.5, SEGMENT_LENGTH / 2);
      concreteParts.push(pillar);
    }

    if (this.tier !== undefined) {
      for (const part of concreteParts) worldScaleUVs(part, part.metadata?.surface === 'metal' ? 1 : 2);
    }

    // Plan 20 P20.2 — the :140 wiring. With a tier the dressing call passes { theme, tier }
    // (lane A's themed vocabulary) and the structures pass merges into the same concrete mesh;
    // with NO tier the call passes NOTHING so lane A's byte-identical no-opts path holds
    // (golden-pinned by world.legacy-golden.json), and structures stay unbuilt — they are a
    // tiered-bridge feature (main.ts wires quality.tier; every real tier then builds them).
    if (this.tier === undefined) {
      concreteParts.push(...buildDressing(scene, index));
    } else {
      concreteParts.push(...buildDressing(scene, index, { theme: this.theme, tier: this.tier }));
      concreteParts.push(...buildStructures(scene, index, this.theme));
    }
    for (const m of concreteParts) if (!m.isVerticesDataPresent(VertexBuffer.ColorKind)) tint(m);

    const asphaltMesh = Mesh.MergeMeshes(asphaltParts, true, true, undefined, false, false)!;
    asphaltMesh.material = materials.asphalt;
    const steel = this.tier === undefined ? [] : concreteParts.filter((part) => part.metadata?.surface === 'metal');
    const props = this.tier === undefined ? [] : concreteParts.filter((part) => part.metadata?.surface === 'prop');
    const concreteMesh = Mesh.MergeMeshes(concreteParts.filter((part) => !steel.includes(part) && !props.includes(part)), true, true, undefined, false, false)!;
    concreteMesh.material = materials.concrete;
    asphaltMesh.parent = root;
    concreteMesh.parent = root;
    let steelMesh: Mesh | undefined;
    if (steel.length) {
      steelMesh = Mesh.MergeMeshes(steel, true, true, undefined, false, false)!;
      steelMesh.name = `bridge-steel-${index}`;
      steelMesh.material = materials.metal;
      steelMesh.parent = root;
    }
    let propMesh: Mesh | undefined;
    if (props.length) {
      propMesh = Mesh.MergeMeshes(props, true, true, undefined, false, false)!;
      propMesh.name = `bridge-props-${index}`;
      propMesh.material = this.propMaterial;
      propMesh.parent = root;
    }
    this.propBatches.push(propMesh);
    this.concreteBatches.push(concreteMesh);
    this.steelBatches.push(steelMesh);
    // NOTE: no freezeWorldMatrix here — the roots move (initial placement + recycling), and
    // freezeWorldMatrix snapshots at freeze time, stranding children at z ∈ [0, 40] (plan 03).
    return root;
  }

  private buildWater(): void {
    const water = MeshBuilder.CreateGround('water', { width: WATER_SIZE, height: WATER_SIZE }, this.scene);
    water.position.y = WATER_Y;
    const bump = this.materials.water.bumpTexture as Texture | null;
    if (bump) {
      bump.uScale = bump.vScale = WATER_SIZE / WATER_TILE;
    }
    water.material = this.materials.water;
    water.isPickable = false;
    this.water = water; // moves with the squad (no freeze)
  }

  /**
   * Far shoreline, hills and a ruined skyline as three concentric cards merged into ONE mesh: one
   * unlit, fog-coloured material (nearer card = darker for aerial perspective). 1 draw.
   * Plan 17 D4: the 'industrial' theme keeps the ring architecture and material but swaps the
   * silhouettes (cooling-tower mounds / container stacks / crenellated gantry masts) and raises the
   * mid/near column counts to 144 — vertex ceiling (73 + 145 + 145) × 2 = 726, still 1 draw.
   */
  private buildHorizon(): void {
    // Each band emits (cols + 1) columns × 2 vertices; overcast is today's code verbatim.
    const layers =
      this.theme === 'industrial'
        ? [
            // Far: cooling towers / reactor blocks — wide, low, hash-gated mounds.
            {
              r: 350,
              cols: 72,
              shade: 0.93,
              h: (i: number): number => {
                const g = hashValue(i, 5, 21);
                const m = hashValue(i, 6, 22);
                return g < 0.3 ? 8 + 14 * m : 3 + 3 * m;
              },
            },
            // Mid: container stacks — boxy skyline quantized to ~1.5 m steps so tops run flat.
            {
              r: 340,
              cols: 144,
              shade: 0.87,
              h: (i: number): number => 1.5 * Math.round((4 + 9 * hashValue(i, 7, 23)) / 1.5),
            },
            // Near: gantry cranes — tall single-column masts with stepped shoulders. A per-column
            // heightfield cannot draw overhangs, so this is a crenellated heightfield, NOT a jib.
            {
              r: 330,
              cols: 144,
              shade: 0.8,
              h: (i: number): number => {
                const mast = 18 + 12 * hashValue(i >> 1, 8, 24); // 18-30 m, shared by the mast/shoulder pair
                return i % 2 === 0 ? mast : mast * 0.4; // stepped shoulder: adjacent column drops to a lower ledge
              },
            },
          ]
        : [
            { r: 350, cols: 72, shade: 0.93, h: (i: number): number => 2 + 5 * hashValue(i >> 1, 1, 11) }, // shoreline (heights are absolute y: the eye is ~y 5, so tops must clear it to show)
            { r: 340, cols: 72, shade: 0.87, h: (i: number): number => 7 + 9 * Math.sin(i * 0.21) * Math.sin(i * 0.07 + 1) ** 2 + 4 * hashValue(i >> 2, 2, 12) }, // hills
            { r: 330, cols: 72, shade: 0.8, h: (i: number): number => (hashValue(i >> 1, 3, 13) < 0.45 ? 4 + 20 * hashValue(i >> 1, 4, 14) : 2) }, // ruined skyline
          ];
    const pos: number[] = [];
    const idx: number[] = [];
    const col: number[] = [];
    for (const L of layers) {
      if (L.r === 330 || this.theme === 'industrial') {
        // Separate flat roof spans read as buildings/gantries instead of triangular mountains.
        for (let i = 0; i < L.cols; i++) {
          const base = pos.length / 3;
          const a = i / L.cols * Math.PI * 2;
          const b = (i + 1) / L.cols * Math.PI * 2;
          const top = L.r === 330 ? landmarkHeight(this.theme, i, L.cols, L.h(i)) : L.h(i);
          for (const [angle, y] of [[a, WATER_Y - 2], [a, top], [b, WATER_Y - 2], [b, top]]) {
            pos.push(Math.sin(angle!) * L.r, y!, Math.cos(angle!) * L.r);
            col.push(L.shade, L.shade, L.shade, 1);
          }
          idx.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
        }
        continue;
      }
      const base = pos.length / 3;
      for (let i = 0; i <= L.cols; i++) {
        const a = (i / L.cols) * Math.PI * 2;
        const x = Math.sin(a) * L.r;
        const z = Math.cos(a) * L.r;
        const top = L.r === 340
          ? [-2, -1, 0, 1, 2].reduce((sum, offset) => sum + L.h((i + offset + L.cols) % L.cols) * (3 - Math.abs(offset)), 0) / 9
          : L.h(i % L.cols);
        pos.push(x, WATER_Y - 2, z, x, top, z);
        col.push(L.shade, L.shade, L.shade, 1, L.shade, L.shade, L.shade, 1);
        if (i < L.cols) idx.push(base + i * 2, base + i * 2 + 1, base + i * 2 + 2, base + i * 2 + 1, base + i * 2 + 3, base + i * 2 + 2);
      }
    }
    // Plan 20 P20.2 (Goal 4): the water glint streak + the dusk-glow sun patch, merged INTO this
    // same ring mesh (still ONE draw, all tiers, per-theme tint). Both are bounded by their own
    // horizon test: glint ≤ 1,200 tris, patch ≤ 1,200, combined ≤ 2,400.
    for (const patch of [horizonGlowGeometry(this.theme), horizonGlintGeometry(this.theme)]) {
      const base = pos.length / 3;
      pos.push(...patch.positions);
      col.push(...patch.colors);
      for (const k of patch.indices) idx.push(k + base);
    }
    const vd = new VertexData();
    vd.positions = pos;
    vd.indices = idx;
    vd.colors = col;
    const mesh = new Mesh('horizon', this.scene);
    vd.applyToMesh(mesh);
    const mat = new StandardMaterial('horizon-mat', this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = this.scene.fogColor.clone();
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.specularColor = new Color3(0, 0, 0);
    mat.fogEnabled = false; // the card IS the fog-tinted haze; fog would paint it the sky colour
    mat.backFaceCulling = false;
    mesh.material = mat;
    mesh.isPickable = false;
    mesh.alwaysSelectAsActiveMesh = true; // ring is centred on the squad; never frustum-culled
    this.horizon = mesh;
  }
}

/** Distinct silhouettes near the route, using existing skyline columns and no extra geometry. */
export function landmarkHeight(theme: EnvTheme, column: number, columns: number, fallback: number): number {
  // Two landmarks straddle the bridge vanishing point; repeat neither around the ring.
  const offset = column <= columns / 2 ? column : column - columns;
  const shape = theme === 'overcast'
    ? [12, 19, 27, 19, 12] // broken waterfront tower
    : theme === 'industrial'
      ? [12, 25, 25, 25, 12, 7, 7, 20, 20] // factory and chimney
      : [8, 15, 25, 13, 21, 11, 8]; // fractured skyline
  const start = theme === 'industrial' ? 5 : 3;
  const at = Math.abs(offset) - start;
  return at >= 0 && at < shape.length ? shape[at]! : fallback;
}

// ---------------------------------------------------------------------------
// Plan 20 P20.2 (Goal 4): horizon increment geometry — the water glint streak and the dusk-glow
// sun patch as pure per-theme vertex/colour strips, merged into the ring mesh by buildHorizon.
//
// Both patches are azimuth-limited strips ON the ring wall (radius just inside the 330 near band,
// so they render in front of it), vertex-tinted from the band shade (0.8) up to a per-theme core.
// Placement is measured against the chase camera (y 4.1) and the continuous deck+shoulder slab
// (y ∈ [-1.1, 0] × |x| ≤ 7.3): dead-ahead the deck occludes the ring below y ≈ −1, so the glow is
// a crowned arch whose wings flank the deck's vanishing point, and the glint sits at azimuth
// ≈ +22° — inside the water wedge that is actually visible beside the bridge (the camera-facing
// side; dead-ahead water is deck-occluded at every distance). No emissive, no new material: the
// patches are ordinary vertex colours on the existing unlit ring material (cores > 1 push the HDR
// read into bloom on High/Medium).
// ---------------------------------------------------------------------------

export interface HorizonPatchGeometry {
  positions: number[];
  indices: number[];
  colors: number[];
}

export const HORIZON_GLINT_TRI_CAP = 1200;
export const HORIZON_GLOW_TRI_CAP = 1200;

/** The near band's shade — patches fade back to it at their edges for a seamless ring. */
const HORIZON_BAND_SHADE = 0.8;

interface HorizonPatchSpec {
  r: number;
  azimuth: number; // centre azimuth (0 = straight ahead, +x positive)
  halfSpan: number;
  cols: number;
  rows: number[]; // y per row, bottom first
  rowBlend: number[]; // 0 = band shade, 1 = full core tint
  core: Record<EnvTheme, [number, number, number]>;
}

const GLINT_SPEC: HorizonPatchSpec = {
  r: 322,
  azimuth: 0.38, // ≈ 22° off-axis — inside the visible water wedge from any camera sway
  halfSpan: 0.11,
  cols: 13,
  rows: [-44, -41, -33, -24],
  rowBlend: [0, 1, 0.55, 0.15],
  core: { overcast: [1.75, 1.85, 2.0], industrial: [1.7, 1.8, 1.85], dusk: [2.3, 1.65, 0.95] },
};

const GLOW_SPEC: HorizonPatchSpec = {
  r: 326,
  azimuth: 0,
  halfSpan: 0.28,
  cols: 25,
  rows: [-44, -18, -6, 2, 8],
  rowBlend: [0, 1, 0.7, 0.4, 0.12],
  core: { overcast: [1.28, 1.16, 1.02], industrial: [1.22, 1.13, 1.02], dusk: [2.3, 1.35, 0.72] },
};

function buildHorizonPatch(spec: HorizonPatchSpec, theme: EnvTheme): HorizonPatchGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const colors: number[] = [];
  const core = spec.core[theme];
  const rowCount = spec.rows.length;
  for (let c = 0; c < spec.cols; c++) {
    const half = (spec.cols - 1) / 2;
    const t = half > 0 ? Math.abs(c - half) / half : 0;
    const side = 1 - t * t; // smooth sideways falloff to the band shade at the edges
    const a = spec.azimuth - spec.halfSpan + (c / (spec.cols - 1)) * spec.halfSpan * 2;
    const x = Math.sin(a) * spec.r;
    const z = Math.cos(a) * spec.r;
    for (let r = 0; r < rowCount; r++) {
      const b = spec.rowBlend[r]! * side;
      positions.push(x, spec.rows[r]!, z);
      colors.push(
        HORIZON_BAND_SHADE + (core[0] - HORIZON_BAND_SHADE) * b,
        HORIZON_BAND_SHADE + (core[1] - HORIZON_BAND_SHADE) * b,
        HORIZON_BAND_SHADE + (core[2] - HORIZON_BAND_SHADE) * b,
        1,
      );
    }
  }
  for (let c = 0; c < spec.cols - 1; c++) {
    for (let r = 0; r < rowCount - 1; r++) {
      const v0 = c * rowCount + r;
      indices.push(v0, v0 + 1, v0 + rowCount, v0 + 1, v0 + rowCount + 1, v0 + rowCount);
    }
  }
  return { positions, indices, colors };
}

/** The water glint streak: a bright per-theme column on the ring at the visible-water azimuth. */
export function horizonGlintGeometry(theme: EnvTheme): HorizonPatchGeometry {
  return buildHorizonPatch(GLINT_SPEC, theme);
}

/** The artistic low-sun glow patch: strongest on 'dusk', subtle on 'overcast'/'industrial'. */
export function horizonGlowGeometry(theme: EnvTheme): HorizonPatchGeometry {
  return buildHorizonPatch(GLOW_SPEC, theme);
}
